import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

/** The one page a builder may put in an iframe on their own website; the same pattern as nginx's frameable location. */
const FRAMEABLE_PATH = /^\/enquire\/[a-z0-9-]+\/?$/;

export function mayRenderInFrame(pathname: string): boolean {
  return FRAMEABLE_PATH.test(pathname);
}

function isFramed(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent can throw on access, which is itself proof of a frame.
    return true;
  }
}

/**
 * The enquiry form is served without X-Frame-Options so a builder can embed it, and from inside
 * that frame the router can still move to any other route (the catch-all, a guard's redirect).
 * Nothing but the form renders inside a frame, so a third-party page cannot overlay the login
 * page or the builder app (clickjacking).
 */
export function FrameGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  if (!isFramed() || mayRenderInFrame(pathname)) return <>{children}</>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 16, background: 'var(--light-bg)' }}>
      <a href={pathname} target="_top" rel="noopener" className="touch-target" style={{ fontSize: 'var(--text-sm)', color: 'var(--orange)' }}>
        Open BuildC in its own window
      </a>
    </div>
  );
}
